import { useEffect, useState, useCallback } from 'react';
import api from '../axiosSetup';
import CustomNavbar from '../CustomNavbar';
import cfg from '../../config';

const formatDate = (d) => {
  if (!d) return 'Kein Datum';
  const date = new Date(d);
  if (isNaN(date.getTime())) return 'Kein Datum';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDt = (dt) =>
  dt ? new Date(dt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

const isOverdue = (dueDate, status) => {
  if (!dueDate || status === 'erledigt') return false;
  return new Date(dueDate) < new Date();
};

const EMPTY_FORM = {
  title: '',
  description: '',
  priority: 'mittel',
  status: 'offen',
  dueDate: '',
  assignedTo: '',
};

// ── Stil-Konstanten ──────────────────────────────────────────
const S = {
  card:       { background: '#1e2130', border: '1px solid #2e3347', borderRadius: 10 },
  cardHeader: { background: '#252839', borderBottom: '1px solid #2e3347', padding: '0.85rem 1.2rem', borderRadius: '10px 10px 0 0' },
  input:      { background: '#13151f', border: '1px solid #3a3f55', color: '#e8eaf0', borderRadius: 6 },
  label:      { color: '#c8cfe0', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 },
  muted:      { color: '#7b82a0', fontSize: '0.82rem' },
  badge: {
    hoch:           { background: '#3a1a1a', color: '#f87171', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    mittel:         { background: '#2a1f0a', color: '#fbbf24', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    niedrig:        { background: '#0a2a0a', color: '#4ade80', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    offen:          { background: '#2a1f0a', color: '#fbbf24', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    inBearbeitung:  { background: '#0a1a3a', color: '#60a5fa', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
    erledigt:       { background: '#0a2a0a', color: '#6ee7b7', fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20 },
  },
};

const PriorityBadge = ({ priority }) => {
  const style = S.badge[priority] || S.badge.mittel;
  return <span style={style}>{priority}</span>;
};

const StatusBadge = ({ status }) => {
  const key = status === 'in Bearbeitung' ? 'inBearbeitung' : status === 'offen' ? 'offen' : 'erledigt';
  const style = S.badge[key] || S.badge.offen;
  return <span style={style}>{status}</span>;
};

const ZRTaskList = () => {
  const [tasks, setTasks]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [deleteId, setDeleteId]     = useState(null);
  const [members, setMembers]       = useState([]);
  const [filterStatus, setFilterStatus]     = useState('alle');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/tasks');
      setTasks(res.data || []);
    } catch {
      setError('Fehler beim Laden der Aufgaben.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMembers = async () => {
    try {
      const res = await api.get('/api/tasks/zunftrat-members');
      setMembers(res.data || []);
    } catch {
      setError(`Fehler beim Laden der ${cfg.memberTermPlural}.`);
    }
  };

  const openCreate = async () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    await loadMembers();
    setShowForm(true);
  };

  const openEdit = async (task) => {
    setError(null);
    await loadMembers();
    setForm({
      title:       task.title,
      description: task.description || '',
      priority:    task.priority,
      status:      task.status,
      dueDate:     task.dueDate ? task.dueDate.slice(0, 10) : '',
      assignedTo:  String(task.assignedTo),
    });
    setEditing(task.id);
    setShowForm(true);
  };

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) { setError('Titel ist erforderlich.'); return; }
    if (!form.assignedTo)   { setError('Empfänger ist erforderlich.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        assignedTo: Number(form.assignedTo),
        dueDate:    form.dueDate || null,
      };
      if (editing) {
        await api.put(`/api/tasks/${editing}`, payload);
        setSuccess('Aufgabe aktualisiert.');
      } else {
        await api.post('/api/tasks', payload);
        setSuccess('Aufgabe erstellt und Benachrichtigung gesendet.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/tasks/${deleteId}`);
      setSuccess('Aufgabe gelöscht.');
      setDeleteId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Löschen.');
      setDeleteId(null);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.patch(`/api/tasks/${taskId}/status`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Ändern des Status.');
    }
  };

  // Client-side filtering
  const statusTabs = ['alle', 'offen', 'in Bearbeitung', 'erledigt'];
  const filtered = tasks.filter(t => {
    if (filterStatus !== 'alle' && t.status !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterAssignee && String(t.assignedTo) !== filterAssignee) return false;
    return true;
  });

  // Unique assignees for filter dropdown
  const uniqueAssignees = Array.from(
    new Map(tasks.map(t => [t.assignedTo, t.assignedToName])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1], 'de'));

  const tabStyle = (key) => ({
    background:   filterStatus === key ? '#ffc107' : 'none',
    color:        filterStatus === key ? '#000' : '#7b82a0',
    border:       `1px solid ${filterStatus === key ? 'transparent' : '#2e3347'}`,
    borderRadius: 20,
    padding:      '5px 14px',
    cursor:       'pointer',
    fontWeight:   filterStatus === key ? 600 : 400,
    fontSize:     '0.85rem',
    transition:   'all 0.15s',
    whiteSpace:   'nowrap',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117' }}>
      <CustomNavbar />
      <div className="container mt-4 pb-5" style={{ maxWidth: 900 }}>

        {/* ── Header ── */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 style={{ color: '#e8eaf0', fontWeight: 700, marginBottom: 2 }}>Aufgabenverwaltung</h2>
            <span style={S.muted}>Aufgaben erstellen, zuweisen und verfolgen</span>
          </div>
          <button
            className="btn btn-primary"
            onClick={openCreate}
            style={{ borderRadius: 8, fontWeight: 600, padding: '8px 18px' }}
          >
            + Neue Aufgabe
          </button>
        </div>

        {error   && <div className="alert alert-danger d-flex justify-content-between align-items-center py-2">{error}<button className="btn-close btn-close-white btn-sm" onClick={() => setError(null)} /></div>}
        {success && <div className="alert alert-success d-flex justify-content-between align-items-center py-2">{success}<button className="btn-close btn-close-white btn-sm" onClick={() => setSuccess(null)} /></div>}

        {/* ── Formular ── */}
        {showForm && (
          <div style={{ ...S.card, marginBottom: 24 }}>
            <div style={S.cardHeader} className="d-flex justify-content-between align-items-center">
              <span style={{ color: '#e8eaf0', fontWeight: 600 }}>
                {editing ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
              </span>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', color: '#7b82a0', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label style={S.label}>Titel <span style={{ color: '#f87171' }}>*</span></label>
                  <input
                    className="form-control" style={S.input}
                    name="title" value={form.title} onChange={handleField}
                    maxLength={255} required placeholder="Aufgabentitel…"
                  />
                </div>
                <div className="mb-3">
                  <label style={S.label}>Beschreibung <span style={S.muted}>(optional)</span></label>
                  <textarea
                    className="form-control" style={S.input}
                    name="description" value={form.description} onChange={handleField}
                    rows={3} placeholder="Details zur Aufgabe…"
                  />
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label style={S.label}>Priorität</label>
                    <select className="form-control" style={S.input} name="priority" value={form.priority} onChange={handleField}>
                      <option value="hoch">Hoch</option>
                      <option value="mittel">Mittel</option>
                      <option value="niedrig">Niedrig</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label style={S.label}>Fälligkeitsdatum <span style={S.muted}>(optional)</span></label>
                    <input
                      type="date" className="form-control" style={S.input}
                      name="dueDate" value={form.dueDate} onChange={handleField}
                    />
                  </div>
                  {editing && (
                    <div className="col-md-4">
                      <label style={S.label}>Status</label>
                      <select className="form-control" style={S.input} name="status" value={form.status} onChange={handleField}>
                        <option value="offen">Offen</option>
                        <option value="in Bearbeitung">In Bearbeitung</option>
                        <option value="erledigt">Erledigt</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label style={S.label}>Zugewiesen an <span style={{ color: '#f87171' }}>*</span></label>
                  <select
                    className="form-control" style={S.input}
                    name="assignedTo" value={form.assignedTo} onChange={handleField} required
                  >
                    <option value="">– {cfg.memberTerm} wählen –</option>
                    {members.map(m => (
                      <option key={m.ID} value={m.ID}>{m.lastName}, {m.firstName}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button type="submit" className="btn btn-primary" disabled={saving} style={{ borderRadius: 7, fontWeight: 600 }}>
                    {saving ? 'Speichern…' : 'Speichern'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    style={{ background: 'none', border: '1px solid #3a3f55', color: '#9aa3bc', borderRadius: 7, padding: '6px 16px', cursor: 'pointer' }}>
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Filter-Tabs ── */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          {statusTabs.map(key => (
            <button key={key} style={tabStyle(key)} onClick={() => setFilterStatus(key)}>
              {key === 'alle' ? `Alle (${tasks.length})` :
               key === 'offen' ? `Offen (${tasks.filter(t => t.status === 'offen').length})` :
               key === 'in Bearbeitung' ? `In Bearbeitung (${tasks.filter(t => t.status === 'in Bearbeitung').length})` :
               `Erledigt (${tasks.filter(t => t.status === 'erledigt').length})`}
            </button>
          ))}
        </div>

        {/* ── Filter-Zeile ── */}
        <div className="d-flex gap-3 mb-4 flex-wrap">
          <select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            style={{ ...S.input, padding: '6px 12px', fontSize: '0.85rem', minWidth: 150 }}
          >
            <option value="">Alle Prioritäten</option>
            <option value="hoch">Hoch</option>
            <option value="mittel">Mittel</option>
            <option value="niedrig">Niedrig</option>
          </select>
          <select
            value={filterAssignee}
            onChange={e => setFilterAssignee(e.target.value)}
            style={{ ...S.input, padding: '6px 12px', fontSize: '0.85rem', minWidth: 200 }}
          >
            <option value="">Alle {cfg.memberTermPlural}</option>
            {uniqueAssignees.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>

        {/* ── Liste ── */}
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: '#ffc107' }} /></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#4a5068', padding: '60px 0' }}>
            {tasks.length === 0 ? 'Noch keine Aufgaben vorhanden.' : 'Keine Aufgaben für diesen Filter.'}
          </div>
        ) : (
          filtered.map(task => {
            const overdue = isOverdue(task.dueDate, task.status);
            return (
              <div key={task.id} style={{ ...S.card, marginBottom: 12 }}>
                <div style={{ padding: '1rem 1.2rem' }}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

                    {/* ── Linke Seite ── */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span style={{ color: '#e8eaf0', fontWeight: 700, fontSize: '1rem' }}>{task.title}</span>
                        <PriorityBadge priority={task.priority} />
                        <StatusBadge status={task.status} />
                      </div>
                      {task.description && (
                        <p style={{ ...S.muted, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {task.description}
                        </p>
                      )}
                      <div className="d-flex flex-wrap gap-3 mt-1">
                        <span style={{ ...S.muted, color: overdue ? '#f87171' : '#7b82a0' }}>
                          Fällig: {formatDate(task.dueDate)}{overdue && ' ⚠'}
                        </span>
                        <span style={S.muted}>Für: {task.assignedToName}</span>
                        <span style={S.muted}>Von: {task.createdByName}</span>
                        <span style={S.muted}>Erstellt: {formatDt(task.createdAt)}</span>
                      </div>
                    </div>

                    {/* ── Rechte Seite: Aktionen ── */}
                    <div className="d-flex gap-2 align-items-center flex-shrink-0 flex-wrap">
                      <select
                        value={task.status}
                        onChange={e => handleStatusChange(task.id, e.target.value)}
                        style={{ ...S.input, padding: '4px 10px', fontSize: '0.82rem', cursor: 'pointer' }}
                        title="Status sofort ändern"
                      >
                        <option value="offen">Offen</option>
                        <option value="in Bearbeitung">In Bearbeitung</option>
                        <option value="erledigt">Erledigt</option>
                      </select>
                      <button
                        onClick={() => openEdit(task)}
                        style={{ background: 'none', border: '1px solid #1a4a8a', color: '#5b9cf6', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => setDeleteId(task.id)}
                        style={{ background: 'none', border: '1px solid #5a2020', color: '#f87171', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* ── Löschen-Bestätigung ── */}
        {deleteId && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ ...S.card, maxWidth: 400, width: '90%', padding: '1.5rem', textAlign: 'center' }}>
              <h5 style={{ color: '#e8eaf0', marginBottom: 8 }}>Aufgabe löschen?</h5>
              <p style={S.muted}>Die Aufgabe wird unwiderruflich gelöscht.</p>
              <div className="d-flex gap-3 justify-content-center mt-3">
                <button onClick={() => setDeleteId(null)}
                  style={{ background: 'none', border: '1px solid #3a3f55', color: '#9aa3bc', borderRadius: 7, padding: '7px 20px', cursor: 'pointer' }}>
                  Abbrechen
                </button>
                <button onClick={handleDelete} className="btn btn-danger" style={{ borderRadius: 7 }}>
                  Löschen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZRTaskList;
